import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { getMsgDeskRuntime } from '@/msgdeskRuntime'
import { useNameScopeValidation } from '@/app/_hooks/useNameScopeValidation'
import { treeStore, type EditorFormInputState } from '@/msgdeskRuntime/state'
import { dispatchEditorAction } from '@/msgdeskRuntime/editorActions'
import type { EditorMode } from '@/msgdeskRuntime/contracts/editorActions'
import type { EditorFormModel, EditorSectionName, EditorSettingsTab } from '@/msgdeskRuntime/contracts/editorModel'

const renderedEditorFieldNames = new Set([
  'name_key_smart',
  'name_key',
  'code_key',
  'subject',
  'to_address',
  'msg_text',
  'attach_file',
  'attach_avatar',
  'is_view',
  'is_view_combo',
  'is_view_msges',
  'is_view_msges_combo',
  'is_view_answers',
  'is_view_answers_combo',
  'is_forbid',
  'is_forbid_combo',
  'is_fold',
  'is_fold_combo',
  'is_freeze',
  'is_freeze_combo',
  'is_hide',
  'is_hide_combo',
  'is_hide_parents',
  'is_one_parent',
  'is_avatar',
  'is_full_tree',
  'is_force_email',
  'is_quote',
  'is_force_pins',
  'is_to_remove',
  'is_left_menu',
  'is_super',
  'is_hidden',
  'is_view_count',
  'is_view_count_0',
  'is_view_count_1',
  'is_view_count_2',
  'is_view_count_3',
  'is_view_count_4',
  'is_view_count_5',
  'is_page_options_color',
  'is_page_options_margin',
  'is_page_options_padding',
  'is_reg_users',
  'is_show_pin',
  'show_pin',
  'is_edit_pin',
  'edit_pin',
  'time_start',
  'time_stop',
  'is_rule_start',
  'is_rule_stop',
  'is_hide_time_locked',
])

export interface EditorTemplateModel {
  inputs: Record<string, EditorFormInputState>
  rough: EditorFormModel | undefined
}

export function useEditorTemplateContract(editorMode?: EditorMode) {
  const [model, setModel] = useState<EditorTemplateModel>(() => ({
    inputs: treeStore.getState().templateData.editorFormInputs || {},
    rough: treeStore.getState().templateData.editorFormModel,
  }))

  useEffect(() => {
    return treeStore.subscribe(() => {
      const templateData = treeStore.getState().templateData
      const nextInputs = templateData.editorFormInputs || {}
      const nextRough = templateData.editorFormModel
      setModel(current => (
        current.inputs === nextInputs && current.rough === nextRough
          ? current
          : { inputs: nextInputs, rough: nextRough }
      ))
    })
  }, [])

  useLayoutEffect(() => {
    const rough = dispatchEditorAction({ action: 'sync-model', reason: 'editor-template:layout' })
    if (rough) setModel(current => current.rough === rough ? current : { ...current, rough })
  }, [editorMode])

  return model
}

export function useEditorFormModelSnapshot(): EditorFormModel | undefined {
  const [model, setModel] = useState(() => treeStore.getState().templateData.editorFormModel)

  useEffect(() => treeStore.subscribe(() => {
    const next = treeStore.getState().templateData.editorFormModel
    setModel(current => current === next ? current : next)
  }), [])

  return model
}

export function useEditorSection(section: EditorSectionName) {
  const model = useEditorFormModelSnapshot()
  const state = model?.sections[section] || {}
  const update = (next: { open?: boolean; activeTab?: EditorSettingsTab }, reason: string) => (
    dispatchEditorAction({ action: 'set-section', section, ...next, reason })
  )
  return { state, update }
}

export function useEditorNameScopeValidation() {
  const model = useEditorFormModelSnapshot()
  return useNameScopeValidation(model?.isMsg && (!model.isUpdate || Number(model.draftElId) > 0)
    ? model.nameScope || '' : '')
}

function setRenderedField(root: Element, name: string, value: unknown): void {
  if (value === undefined || value === null || typeof value === 'object') return
  const elements = root.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(`[name="${CSS.escape(name)}"]`)
  elements.forEach(element => {
    if (element instanceof HTMLInputElement && (element.type === 'checkbox' || element.type === 'radio')) {
      const nextChecked = Boolean(value)
      if (element.checked !== nextChecked) element.checked = nextChecked
      return
    }
    // Equality guard: the rough overlay re-runs on model-identity changes
    // (every synced input produces a new model), and re-assigning an identical
    // textarea/input value would still reset the caret to the end mid-typing.
    let nextValue = String(value)
    if (element instanceof HTMLSelectElement) {
      // m31 display rule: a stored 0 (pair unchecked) still displays option 1.
      const numeric = Number(value)
      if (Number.isFinite(numeric) && numeric < 1) nextValue = '1'
    }
    if (element.value !== nextValue) element.value = nextValue
  })
}

function applyRoughModel(root: Element, model: EditorFormModel | undefined, editorMode?: EditorMode): void {
  if (!model || (editorMode && model.mode && model.mode !== editorMode)) return
  setRenderedField(root, 'code_key', model.fields.codeKey)
  setRenderedField(root, 'subject', model.fields.subject)
  setRenderedField(root, 'name_key', model.fields.nameKey)
  setRenderedField(root, 'msg_text', model.fields.bodyText)
  setRenderedField(root, 'name_key_smart', model.fields.smartNameKey)
  setRenderedField(root, 'to_address', model.fields.recipientInput)

  Object.entries(model.settings).forEach(([name, value]) => setRenderedField(root, name, value))

  setRenderedField(root, 'is_show_pin', model.pins.show.enabled)
  setRenderedField(root, 'show_pin', model.pins.show.value)
  setRenderedField(root, 'is_edit_pin', model.pins.edit.enabled)
  setRenderedField(root, 'edit_pin', model.pins.edit.value)

  setRenderedField(root, 'time_start', model.ruling.timeStart)
  setRenderedField(root, 'time_stop', model.ruling.timeStop)
  setRenderedField(root, 'is_rule_start', model.ruling.startEnabled)
  setRenderedField(root, 'is_rule_stop', model.ruling.stopEnabled)
  setRenderedField(root, 'is_hide_time_locked', model.ruling.hideTimeLocked)
}

export function EditorFormDefaults({ inputs, rough, editorMode }: EditorTemplateModel & { editorMode?: EditorMode }) {
  const hydratedRef = useRef<
    { root: Element; mode?: EditorMode; inputs: Record<string, EditorFormInputState> } | null
  >(null)
  const appliedRoughRef = useRef<EditorFormModel | undefined>(undefined)

  useLayoutEffect(() => {
    const root = getMsgDeskRuntime().querySelector('[data-md-contract="editor.root"]')
    if (!root) return
    const roughMatchesMode = Boolean(rough && (!editorMode || !rough.mode || rough.mode === editorMode))
    const hydrated = hydratedRef.current
    // Parsed defaults hydrate once per mounted editor/mode/input identity —
    // re-applying them on ordinary store notifies would stamp empty defaults
    // over what the user just typed. The rough overlay instead re-applies on
    // every NEW model identity: legacy fills the rough asynchronously after
    // show_form (show_form_2 via the rich-editor oninit), so the first
    // snapshot can be a default-empty model that must not stay latched.
    // setRenderedField's equality guards make identical re-stamps no-ops.
    const needsInputs = !(hydrated && hydrated.root === root && hydrated.mode === editorMode && hydrated.inputs === inputs)
    const needsRough = roughMatchesMode && appliedRoughRef.current !== rough
    if (!needsInputs && !needsRough) return

    if (needsInputs) {
      for (const field of Object.values(inputs)) {
        const elements = root.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(`[name="${CSS.escape(field.name)}"]`)
        elements.forEach(element => {
          if (element instanceof HTMLInputElement && (element.type === 'checkbox' || element.type === 'radio')) {
            element.checked = !!field.checked
            return
          }

          if (element instanceof HTMLSelectElement && field.selectedValues?.length) {
            const selected = new Set(field.selectedValues)
            for (const option of Array.from(element.options)) option.selected = selected.has(option.value)
            return
          }

          if (field.value !== undefined) element.value = field.value
        })
      }
    }

    // Parsed legacy defaults establish the form contract; the live rough is
    // the authoritative per-mode editor state and must win over them.
    if (roughMatchesMode || needsInputs) {
      applyRoughModel(root, rough, editorMode)
      if (roughMatchesMode) appliedRoughRef.current = rough
    }
    hydratedRef.current = { root, mode: editorMode, inputs }
  }, [editorMode, inputs, rough])

  return null
}

export function HiddenEditorFormFields({ inputs }: { inputs: Record<string, EditorFormInputState> }) {
  return (
    <>
      {Object.values(inputs)
        .filter(field => !renderedEditorFieldNames.has(field.name) && field.type !== 'file')
        .map(field => (
          <input key={field.name} type="hidden" name={field.name} value={field.value ?? ''} />
        ))}
    </>
  )
}
